import type { Schema } from "./api";

/** Presentation rules for СВОР that do not need React: labels, the Было/Минус/Плюс/Стало cells and row filters.
 * Runtime imports stay out so the module runs under the plain Node test runner like pdfRegions. */
const format = (value: string) => Number(value).toLocaleString("ru-RU", { maximumFractionDigits: 8 });
export type SvorRow = Schema["SvorRow"];
export type SourceKind = Schema["SvorSourceOut"]["kind"];
export type RowState = NonNullable<SvorRow["state"]>;
export type Role = NonNullable<Schema["PreviewColumn"]["role"]>;
export type QuantityColumn = "was" | "minus" | "plus" | "result";
export type QuantityTone = "value" | "zero" | "unknown" | "absent" | "correction";
export type RowFilter = "all" | "changes" | "attention";
export type Mapping = Partial<Record<string, string>>;

export const formula = "Стало = Было − Минус + Плюс";
export const kinds: SourceKind[] = ["original", "excluded", "added"];
export const kindLabels: Record<SourceKind, string> = {
  original: "Исходная смета",
  excluded: "И — исключения",
  added: "Д — добавления и корректировки",
};
export const kindNotes: Record<SourceKind, string> = {
  original: "Хранится байт в байт и не изменяется. Новая загрузка не перезаписывает файл: прежний источник остаётся в истории, а раунд считается по принятому.",
  excluded: "Позиции, исключаемые из исходной сметы, — колонка «Минус». Исключение применяется только при строгом совпадении номера, раздела, подраздела, шифра и содержания; иначе оно попадает в протокол разногласий.",
  added: "Добавленные и корректирующие позиции — колонка «Плюс». Отрицательное значение — корректировка в меньшую сторону; новая позиция 276.1 встаёт после 276, остальные закрывают свой раздел.",
};
export const stateLabels: Record<RowState, string> = {
  unchanged: "Без изменений", changed: "Изменено", excluded: "Исключено", added: "Добавлено", attention: "Требует проверки",
};
export const stateClass: Record<RowState, string> = {
  unchanged: "", changed: "approved", excluded: "rejected", added: "approved", attention: "needs_review",
};
export const roles: { role: Role; label: string; required: boolean }[] = [
  { role: "position", label: "Номер позиции (№ в ЛСР)", required: false },
  { role: "description", label: "Наименование работы", required: true },
  { role: "quantity", label: "Объём", required: true },
  { role: "unit", label: "Единица", required: true },
  { role: "code", label: "Шифр нормы", required: false },
  { role: "basis", label: "Обоснование / ссылка на техчасть", required: false },
  { role: "formula", label: "Формула расчёта", required: false },
];

/** Reasons the parser would reject the mapping, so the button explains them before the request. */
export function mappingProblems(mapping: Mapping): string[] {
  const problems = roles.filter(item => item.required && !mapping[item.role]).map(item => `не назначена колонка «${item.label}»`);
  const used = new Map<string, string[]>();
  for (const { role, label } of roles) {
    const letter = mapping[role];
    if (letter) used.set(letter, [...(used.get(letter) ?? []), label]);
  }
  for (const [letter, labels] of used) {
    if (labels.length > 1) problems.push(`колонка ${letter} назначена дважды: ${labels.join(", ")}`);
  }
  return problems;
}

/** Only the roles shown in the form, without blanks: the parser rejects unknown roles and empty letters. */
export function cleanMapping(mapping: Mapping): Record<string, string> {
  const known = new Set<string>(roles.map(item => item.role));
  const entries = Object.entries(mapping).filter((entry): entry is [string, string] => known.has(entry[0]) && !!entry[1]);
  return Object.fromEntries(entries);
}

/** Blank never becomes zero: Было/Стало without a value are unknown, Минус/Плюс without a value have no change source. */
export function quantityCell(value: string | null | undefined, column: QuantityColumn): { text: string; tone: QuantityTone; note: string } {
  if (value == null) {
    return column === "was" || column === "result"
      ? { text: "не установлено", tone: "unknown", note: "Объём не задан в источнике; пустое значение не заменяется нулём" }
      : { text: "—", tone: "absent", note: "Источник изменения по этой позиции отсутствует" };
  }
  const numeric = Number(value);
  if (numeric === 0) return { text: "0", tone: "zero", note: "Явный ноль" };
  if (column === "plus" && numeric < 0) {
    return { text: format(value), tone: "correction", note: "Отрицательное значение в Д — корректировка в меньшую сторону" };
  }
  return { text: format(value), tone: "value", note: "" };
}

/** Positions by filter; a section or subsection heading stays only while it still introduces a visible position. */
export function filterRows<T extends { kind: SvorRow["kind"]; state?: SvorRow["state"] }>(rows: T[], filter: RowFilter): T[] {
  const visible = (row: T) => filter === "all" || (filter === "changes" ? row.state !== "unchanged" : row.state === "attention");
  const kept: T[] = [];
  let sinceSection = false, sinceSubsection = false;
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const row = rows[index];
    if (row.kind === "position") {
      if (visible(row)) { kept.push(row); sinceSection = sinceSubsection = true; }
    } else if (row.kind === "subsection") {
      if (sinceSubsection) kept.push(row);
      sinceSubsection = false;
    } else {
      if (sinceSection) kept.push(row);
      sinceSection = sinceSubsection = false;
    }
  }
  return kept.reverse();
}

export function remarkSummary<T extends { label: string; carried_from?: string | null }>(remarks: T[]): { label: string; count: number; carried: number }[] {
  const groups = new Map<string, { label: string; count: number; carried: number }>();
  for (const remark of remarks) {
    const group = groups.get(remark.label) ?? { label: remark.label, count: 0, carried: 0 };
    group.count += 1;
    if (remark.carried_from) group.carried += 1;
    groups.set(remark.label, group);
  }
  return [...groups.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "ru"));
}

export function roundHistoryText(round: { resolved: number; remaining: number; new: number }): string {
  return `устранено ${round.resolved} · осталось ${round.remaining} · новых ${round.new}`;
}

type SourceLike = { kind: string; active: boolean; status: string };

export function importedSource<T extends SourceLike>(sources: T[], kind: SourceKind): T | undefined {
  return sources.find(source => source.kind === kind && source.active && source.status === "imported");
}

export function pendingSources<T extends SourceLike>(sources: T[], kind: SourceKind): T[] {
  return sources.filter(source => source.kind === kind && source.active && source.status === "uploaded");
}

export function replacedSources<T extends SourceLike>(sources: T[], kind: SourceKind): T[] {
  return sources.filter(source => source.kind === kind && !source.active);
}

export function setStateText(set: { sources_present: string[]; latest_round: { number: number; state: string } | null }): string {
  if (!set.latest_round) return set.sources_present.includes("original") ? "раунд не рассчитан" : "исходная смета не принята";
  return `раунд ${set.latest_round.number} · ${set.latest_round.state === "clear" ? "замечаний нет" : "есть замечания"}`;
}
